/**
 * FormBuilder field config for duration (numberStepper).
 * Generic — name and label must be provided by consumer.
 */
import { useMemo } from 'react';

interface UseDurationFieldConfigProps {
  name: string;
  label: string;
  condition?: Record<string, unknown>;
  min?: number;
  max?: number;
  step?: number;
  unitLabel?: string;
}

export function useDurationFieldConfig(props: UseDurationFieldConfigProps) {
  const min = props.min ?? 5;
  const max = props.max ?? 1440;
  const step = props.step ?? 15;
  const unitLabel = props.unitLabel ?? 'mins';

  const fieldConfig = useMemo(
    () => ({
      type: 'numberStepper' as const,
      name: props.name,
      label: props.label,
      condition: props.condition,
      // `min` / `max` MUST live at the top level too — the kula-ai
      // FormBuilder's Zod schema validates against `e.min` / `e.max`
      // (top-level), not `e.fieldProps.min`. Without this the field
      // accepts any value and the recruiter only finds out at save
      // time. Adapter still reads `fieldProps?.min ?? min`, so keeping
      // them in both places is safe and matches the convention the
      // settings buffer field already uses.
      min,
      max,
      fieldProps: {
        step,
        min,
        max,
        unitLabel,
      },
    }),
    [props.name, props.label, props.condition, step, min, max, unitLabel]
  );

  return { fieldConfig };
}
